import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';

export interface TeamMember {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  role?: 'owner' | 'admin' | 'reviewer';
}

export interface TeamInvite {
  id: string;
  email: string;
  team_id: string;
  created_at: string;
}

export const useTeamMembers = () => {
  const { selectedTeam } = useWorkspace();
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!selectedTeam || !user) return;

      setIsLoading(true);
      setError(null);

      try {
        // Fetch team members
        const { data: teamData, error: teamError } = await supabase
          .from('Teams')
          .select('users')
          .eq('id', selectedTeam.id)
          .single();

        if (teamError) throw teamError;

        if (teamData?.users && teamData.users.length > 0) {
          // Fetch profiles for all team members
          const { data: profiles, error: profilesError } = await supabase
            .from('Profiles')
            .select('*')
            .in('user_id', teamData.users);

          if (profilesError) throw profilesError;

          setMembers(profiles || []);
        } else {
          setMembers([]);
        }

        // Fetch pending invites
        const { data: invitesData, error: invitesError } = await supabase
          .from('UserInvites')
          .select('*')
          .eq('team_id', selectedTeam.id);

        if (invitesError) throw invitesError;

        setInvites(invitesData || []);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamMembers();
  }, [selectedTeam, user]);

  return {
    members,
    invites,
    isLoading,
    error,
    setMembers,
    setInvites,
    setError
  };
}; 